import React, { useState } from "react";
import CapitalizationUtil from "../../utils/CapitalizationUtil.ts";
import Loader from "../../common/Loader";

interface OfficeNode {
  parentid: number;
  name: string;
  children: { [key: string]: OfficeNode };
}

interface Office {
  [key: string]: OfficeNode;
}

interface TreeViewProps {
  data: Office;
  isLoading: boolean;
}

const TreeView: React.FC<TreeViewProps> = ({ data, isLoading }) => {
  const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>({
    3: true,
  });

  const handleToggle = (key: string) => {
    setExpandedKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderTree = (
    nodes: { [key: string]: OfficeNode },
    depth: number = 0
  ) => {
    return Object.keys(nodes).map((key) => {
      const item = nodes[key];
      const hasChildren =
        item.children && Object.keys(item.children).length > 0;
      const isExpanded = expandedKeys[key] || false;

      const normalizedName = CapitalizationUtil.toTitleCase(item?.name);

      const marginBottom = depth === 0 ? "mb-3" : depth === 1 ? "mb-3" : "mb-2";
      const paddingLeft = depth > 0 ? `pl-${depth * 4}` : "";

      return (
        <li
          key={key}
          className={`hover:bg-secondary-100 ${marginBottom} px-1 ${paddingLeft}`}
        >
          {hasChildren ? (
            <>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleToggle(key);
                }}
                className="hover:bg-secondary-100 focus:text-primary active:text-primary flex items-center"
              >
                <i
                  className={`mr-2 ${
                    isExpanded
                      ? "fa-solid fa-building"
                      : "fa-regular fa-building"
                  }`}
                />
                {normalizedName}
              </a>
              <ul className={`ml-4 ${isExpanded ? "block" : "hidden"}`}>
                {renderTree(item.children, depth + 1)}
              </ul>
            </>
          ) : (
            <span className="flex items-center">
              <i
                className={`fa-solid opacity-35 mr-2 ${
                  normalizedName.length > 0 ? "visible" : "hidden"
                } ${depth > 1 ? "fa-arrow-turn-down-right" : "fa-dash"}`}
              />
              {normalizedName}
            </span>
          )}
        </li>
      );
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  return <ul>{renderTree(data)}</ul>;
};

export default TreeView;
